// Inventory Recon — opening → closing variance tracker.
//
// Layout:
//   Filters (Start/End/Brand/SKU) → 10 overview cards → SKU-level table.
//
// SKU table columns:
//   ASIN | BRAND | SKU
//   Opening Stock
//   Bulk Dispatch [+] · B2C Orders [+] · RTO [+] · RTV [+]   ← expandable
//   GRN · F2 → F1
//   Ideal Closing Stock · Closing Stock · Variance
// Click the [+] chevron on any expandable header to reveal per-channel sub-
// columns (Amazon · Flipkart · Blinkit · W.in · Offline · Zepto · Meesho ·
// FK Mins · AZ Now) for that metric.
//
// Data-source status: Bulk Dispatch is wired live across the 8 existing
// dispatch tabs (FBA / FBF / Blinkit / W.in / Zepto / FK Mins / Offline /
// AZ Now). All other metrics are at zero with `Source TBD` badges; the
// calculation engine lives in `/lib/recon-config.js` (computeSkuDerived /
// computeDerived) and is fully ready — wire a fetcher and the totals,
// formulas, sorting, totals row and Excel export light up for free.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import MultiSelect from '@/components/MultiSelect';
import { toast } from 'sonner';
import { RefreshCw, Download, Plus, Minus, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  RECON_METRICS, TONE_OUTLINE, RECON_CHANNELS, DISPATCH_TO_CHANNEL, RTV_SOURCES,
  RTO_CHANNEL_MAP, B2C_SOURCES, EXPANDABLE_METRIC_KEYS, channelsForMetric,
  emptyReconSkuRow, computeSkuDerived,
  isReconDropType, isReconShakerDrop, SYNTHETIC_RECON_SKUS,
  SECTION_STYLES, SECTION_TOTAL_HIGHLIGHT,
} from '@/lib/recon-config';
import { DISPATCH_SOURCES } from '@/lib/projections-config';
import { findColumnIndex, parseNumber } from '@/lib/channel-intel';
import { norm, formatInt } from '@/lib/format-utils';
import { todayISO, toISO, parseLocalDate, parseAnyDate, addDays } from '@/lib/date-utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISPATCH_KEYS = ['blinkit', 'fbf', 'fba', 'offline', 'fk_mins', 'zepto', 'win', 'az_now'];

export default function InventoryReconPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const initialEnd = parseLocalDate(todayISO());
  const initialStart = addDays(initialEnd, -6);
  // Transaction range: powers all flow metrics (Bulk Dispatch · B2C · RTO ·
  // RTV · GRN · F2→F1). Inventory range is independent and powers Opening /
  // Closing Stock only (D-1 of start = Opening, D-1 of end = Closing).
  const [startDate, setStartDate] = useState(toISO(initialStart));
  const [endDate, setEndDate] = useState(toISO(initialEnd));
  const [invStartDate, setInvStartDate] = useState(toISO(initialStart));
  const [invEndDate, setInvEndDate]     = useState(toISO(initialEnd));
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [selectedAsins, setSelectedAsins] = useState([]);
  const [overviewRows, setOverviewRows] = useState([]);
  const [dispatchData, setDispatchData] = useState({});
  // RTV sources from `/api/sheets/rtv` keyed by RTV_SOURCES[].key
  const [rtvData, setRtvData] = useState({});
  // GRN INHOUSE tab — single source, per-row Title → SKU + units.
  const [grnRows, setGrnRows] = useState([]);
  // F2 → F1 ledger ("RTO to F01 from APRIL 2025") — per-row ASIN → SKU + units.
  const [f2ToF1Rows, setF2ToF1Rows] = useState([]);
  // DSR tab — per-SKU daily inventory snapshots; powers Opening & Closing Stock.
  const [dsrRows, setDsrRows] = useState([]);
  // RTO sources keyed by tab — `current` = Consumer Returns (Jul'26+),
  // `apr2026` / `may2026` / `jun2026` = per-month backfill tabs. The
  // aggregator uses a strict mutually-exclusive month mask to prevent
  // double counting (April rows accepted only from `apr2026`, May only
  // from `may2026`, June only from `jun2026`, everything else from
  // `current`).
  const [rtoSources, setRtoSources] = useState({});
  // B2C Orders: per-channel pivot tabs (Title | SKU 1..4 | date headers).
  const [b2cData, setB2cData] = useState({});

  // Table state
  const [expandedCols, setExpandedCols] = useState(() => new Set());
  const [sortKey, setSortKey] = useState('sku');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    const token = localStorage.getItem('radar_session_token');
    if (!token) { navigate('/'); return; }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('radar_session_token');
      const ts = Date.now();
      const headers = {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      };
      const cfg = { headers, params: { _t: ts } };
      const dispatchPromises = DISPATCH_KEYS.map(k =>
        k === 'blinkit'
          ? axios.get(`${API}/sheets/blinkit-planned`, cfg)
          : axios.get(`${API}/sheets/dispatch/${k}`, cfg)
      );
      const [ov, mapping, rtv, grn, f2tof1, dsr, rto, b2c, ...dispatchResults] = await Promise.all([
        axios.get(`${API}/sheets/data`, cfg),
        axios.get(`${API}/sheets/dispatch/mappings`, cfg),
        axios.get(`${API}/sheets/rtv`, cfg),
        axios.get(`${API}/sheets/grn`, cfg),
        axios.get(`${API}/sheets/f2-to-f1`, cfg),
        axios.get(`${API}/sheets/dsr`, cfg),
        axios.get(`${API}/sheets/rto`, cfg),
        axios.get(`${API}/sheets/b2c`, cfg),
        ...dispatchPromises,
      ]);
      setOverviewRows(ov.data.rows || []);
      const dispatch = { mappings: { rows: mapping.data.rows || [] } };
      DISPATCH_KEYS.forEach((k, i) => {
        dispatch[k] = { rows: dispatchResults[i].data.rows || [] };
      });
      setDispatchData(dispatch);
      setRtvData(rtv.data?.sources || {});
      setGrnRows(grn.data?.rows || []);
      setF2ToF1Rows(f2tof1.data?.rows || []);
      setDsrRows(dsr.data?.rows || []);
      setRtoSources(rto.data?.sources || {});
      setB2cData(b2c.data?.sources || {});
      const rtvErrors = rtv.data?.errors || [];
      if (rtvErrors.length > 0) {
        console.warn('[recon] some RTV tabs unreadable:', rtvErrors);
      }
      toast.success('Recon data loaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch recon data');
    } finally {
      setLoading(false);
    }
  };

  // ============ Brand & SKU filter options ============
  const overviewDicts = useMemo(() => {
    if (!overviewRows.length) return [];
    const headers = overviewRows[0];
    return overviewRows.slice(1).map(r => {
      const o = {}; headers.forEach((h, i) => { o[h] = r[i] !== undefined ? r[i] : ''; });
      return o;
    });
  }, [overviewRows]);

  const brandOptions = useMemo(() => {
    const set = new Set();
    overviewDicts.forEach(r => { if (r.BRAND) set.add(r.BRAND); });
    return [...set].sort().map(b => ({ value: b, label: b }));
  }, [overviewDicts]);

  // ASIN options narrow with the brand filter (and SKU filter, when present)
  // so the dropdown only ever exposes ASINs that are actually still selectable
  // in the current visible set. Matches how `skuOptions` narrows by brand.
  const asinOptions = useMemo(() => {
    const rows = overviewDicts.filter(r => {
      if (selectedBrands.length && !selectedBrands.includes(r.BRAND)) return false;
      if (selectedSkus.length   && !selectedSkus.includes((r.SKU || '').toString().trim())) return false;
      return true;
    });
    const set = new Set();
    rows.forEach(r => { const a = (r.ASIN || '').toString().trim(); if (a) set.add(a); });
    return [...set].sort().map(a => ({ value: a, label: a }));
  }, [overviewDicts, selectedBrands, selectedSkus]);

  const skuOptions = useMemo(() => {
    const rows = overviewDicts.filter(r => {
      if (selectedBrands.length && !selectedBrands.includes(r.BRAND)) return false;
      if (selectedAsins.length  && !selectedAsins.includes((r.ASIN || '').toString().trim())) return false;
      return true;
    });
    const set = new Set();
    rows.forEach(r => { const s = (r.SKU || '').toString().trim(); if (s) set.add(s); });
    return [...set].sort().map(s => ({ value: s, label: s }));
  }, [overviewDicts, selectedBrands, selectedAsins]);

  // ============ Mappings: id → canonical SKU title ============
  const inventorySkuSet = useMemo(() => {
    const set = new Set();
    overviewDicts.forEach(r => { const s = norm(r.SKU); if (s) set.add(s); });
    // Synthetic recon SKUs (e.g., "Pack of 1" Wafer Bars) live outside the
    // canonical inventory master but operations wants them tracked on this
    // page — include them so B2C / RTO / dispatch resolution can find them.
    SYNTHETIC_RECON_SKUS.forEach(r => { const s = norm(r.sku); if (s) set.add(s); });
    return set;
  }, [overviewDicts]);

  const titleByKey = useMemo(() => {
    const rows = dispatchData.mappings?.rows;
    if (!rows || rows.length < 2) return {};
    const hdr = rows[0];
    const idx = {
      asin:   findColumnIndex(hdr, ['asin']),
      fsn:    findColumnIndex(hdr, ['fsn']),
      winSku: findColumnIndex(hdr, ['w.in sku', 'win sku']),
      title:  findColumnIndex(hdr, ['title']),
    };
    const out = {};
    const confirmed = {};
    const setMapping = (rawKey, title) => {
      if (!rawKey || !title) return;
      const k = norm(rawKey);
      const t = norm(title);
      if (!k || !t) return;
      const isConfirmed = inventorySkuSet.has(t);
      if (!(k in out) || (isConfirmed && !confirmed[k])) {
        out[k] = t;
        confirmed[k] = isConfirmed;
      }
    };
    rows.slice(1).forEach(r => {
      const title = idx.title !== -1 ? (r[idx.title] || '').toString().trim() : '';
      if (!title) return;
      setMapping(title, title);
      if (idx.asin   !== -1) setMapping(r[idx.asin],   title);
      if (idx.fsn    !== -1) setMapping(r[idx.fsn],    title);
      if (idx.winSku !== -1) setMapping(r[idx.winSku], title);
    });
    return out;
  }, [dispatchData, inventorySkuSet]);

  // ============ Build SKU-level recon rows (calc engine entry-point) ============
  // The engine seeds one row per inventory SKU (so filters reliably zero-out
  // SKUs that match no dispatch data) and accumulates Bulk Dispatch per
  // channel. All other metrics stay at 0 — wiring them later is a matter of
  // adding similar accumulator loops, the rest (sorting, totals, derived
  // math, Excel export) lights up for free.
  const skuRows = useMemo(() => {
    if (!overviewDicts.length) return [];
    if (!startDate || !endDate) return [];
    const start = parseLocalDate(startDate); start?.setHours(0, 0, 0, 0);
    const end   = parseLocalDate(endDate);   end?.setHours(23, 59, 59, 999);
    if (!start || !end) return [];

    // Seed rows from overview_source, applying Brand/SKU filters up-front.
    // Recon-specific exclusion: drop channel-level shaker rows so totals only
    // include the parent rollups (Steel Shakers / Glass Bottle Shaker).
    const rowsByTitle = new Map();
    let shakerDropped = 0;
    overviewDicts.forEach(r => {
      const sku = (r.SKU || '').toString().trim();
      if (!sku) return;
      if (isReconShakerDrop(sku)) { shakerDropped += 1; return; }
      if (selectedBrands.length > 0 && !selectedBrands.includes(r.BRAND)) return;
      if (selectedAsins.length  > 0 && !selectedAsins.includes((r.ASIN || '').toString().trim())) return;
      if (selectedSkus.length   > 0 && !selectedSkus.includes(sku)) return;
      const titleKey = norm(sku);
      if (!titleKey || rowsByTitle.has(titleKey)) return;
      rowsByTitle.set(titleKey, emptyReconSkuRow(r.ASIN || '', r.BRAND || '', sku));
    });
    // Seed synthetic recon SKUs (B2C-only consumption with no inventory
    // master entry). They obey the same Brand/ASIN/SKU filters and the shaker
    // drop rule, so users can still filter them out from the UI.
    SYNTHETIC_RECON_SKUS.forEach(r => {
      const sku = (r.sku || '').toString().trim();
      if (!sku) return;
      if (isReconShakerDrop(sku)) return;
      if (selectedBrands.length > 0 && !selectedBrands.includes(r.brand)) return;
      if (selectedAsins.length  > 0 && !selectedAsins.includes((r.asin || '').toString().trim())) return;
      if (selectedSkus.length   > 0 && !selectedSkus.includes(sku)) return;
      const titleKey = norm(sku);
      if (!titleKey || rowsByTitle.has(titleKey)) return;
      rowsByTitle.set(titleKey, emptyReconSkuRow(r.asin || '', r.brand || '', sku));
    });
    if (shakerDropped > 0) {
      console.log(`[recon] Shaker-rule: dropped ${shakerDropped} channel-level shaker rows; kept only Steel Shakers + Glass Bottle Shaker parents`);
    }

    // Accumulate Bulk Dispatch from each dispatch tab.
    DISPATCH_KEYS.forEach(key => {
      const src = DISPATCH_SOURCES[key];
      const sheet = dispatchData[key];
      const channelLabel = DISPATCH_TO_CHANNEL[key];
      if (!src || !sheet || !channelLabel || !sheet.rows || sheet.rows.length < 2) return;
      const hdr = sheet.rows[0];
      const asArr = (v) => Array.isArray(v) ? v : [v];
      const dateIdx  = findColumnIndex(hdr, asArr(src.dateCol));
      const idIdx    = findColumnIndex(hdr, asArr(src.idCol));
      const unitsIdx = findColumnIndex(hdr, asArr(src.unitsCol));
      if (dateIdx === -1 || idIdx === -1 || unitsIdx === -1) return;

      sheet.rows.slice(1).forEach(row => {
        const d = parseAnyDate(row[dateIdx]);
        if (!d || d < start || d > end) return;
        const id = row[idIdx];
        const k = norm(id);
        if (!k) return;
        // Resolve identifier → canonical SKU title via Mappings.
        const title = titleByKey[k] || (inventorySkuSet.has(k) ? k : null);
        if (!title) return;
        const target = rowsByTitle.get(title);
        if (!target) return; // SKU not in the current filtered set
        const units = parseNumber(row[unitsIdx]);
        target.bulkDispatch.byChannel[channelLabel] += units;
        target.bulkDispatch.total += units;
      });
    });

    // ============ Accumulate RTV per (SKU × channel) =====================
    // Walks each configured RTV tab, applies the SAME date-range / Brand /
    // SKU filter logic as Bulk Dispatch, resolves the row's identifier
    // (ASIN or SKU) to a canonical title via Mappings, and adds units to
    // `rtv.byChannel[channel]` + `rtv.total`. Rows whose id doesn't resolve
    // to a SKU in the inventory master are silently dropped (consistent
    // with how Bulk Dispatch handles unknowns).
    //
    // Per-source counters power the validation log emitted below. They make
    // mismatches (e.g. "FK Mins Apr'26 should be ~1,903") debuggable without
    // ever leaving the browser console — every row in every tab is accounted
    // for as one of: kept, out-of-range, unparseable-date, no-id, no-units,
    // unmapped (id not in Mappings), or filtered-out (SKU/Brand filter).
    const rtvByChannel = {};   // channel → units (post date-range)
    const rtvBySku = new Map();// canonical title → units (post date-range)
    const rtvPerSource = [];   // diagnostic table for the console log
    let rtvTotalUnits = 0;

    RTV_SOURCES.forEach(src => {
      const sheet = rtvData[src.key];
      const stats = {
        key: src.key, tab: src.tab, channel: src.channel,
        totalRows: 0, kept: 0, units: 0,
        skippedDateUnparseable: 0, skippedOutOfRange: 0,
        skippedNoId: 0, skippedNoUnits: 0,
        skippedUnmapped: 0, skippedFilteredOut: 0,
        missingCols: false,
      };
      rtvPerSource.push(stats);
      if (!sheet || !sheet.rows || sheet.rows.length < 2) { stats.missingCols = !sheet; return; }
      const hdr = sheet.rows[0];
      const dateIdx  = findColumnIndex(hdr, [src.dateCol]);
      const idIdx    = findColumnIndex(hdr, [src.idCol]);
      const unitsIdx = findColumnIndex(hdr, [src.unitsCol]);
      if (dateIdx === -1 || idIdx === -1 || unitsIdx === -1) {
        stats.missingCols = true;
        console.warn(`[recon] RTV "${src.tab}" missing required column(s) → date:${dateIdx} id:${idIdx} units:${unitsIdx}`);
        return;
      }
      sheet.rows.slice(1).forEach(row => {
        stats.totalRows += 1;
        const d = parseAnyDate(row[dateIdx]);
        if (!d) { stats.skippedDateUnparseable += 1; return; }
        if (d < start || d > end) { stats.skippedOutOfRange += 1; return; }
        const id = row[idIdx];
        const k = norm(id);
        if (!k) { stats.skippedNoId += 1; return; }
        const units = parseNumber(row[unitsIdx]);
        if (!units) { stats.skippedNoUnits += 1; return; }
        const title = titleByKey[k] || (inventorySkuSet.has(k) ? k : null);
        if (!title) { stats.skippedUnmapped += 1; return; }
        const target = rowsByTitle.get(title);
        if (!target) { stats.skippedFilteredOut += 1; return; }
        // Channel comes from the source config, NOT the row data.
        if (!(src.channel in target.rtv.byChannel)) {
          target.rtv.byChannel[src.channel] = 0;
        }
        target.rtv.byChannel[src.channel] += units;
        target.rtv.total += units;
        stats.kept += 1;
        stats.units += units;
        rtvByChannel[src.channel] = (rtvByChannel[src.channel] || 0) + units;
        rtvBySku.set(title, (rtvBySku.get(title) || 0) + units);
        rtvTotalUnits += units;
      });
    });

    // ---- Validation log + cross-check ----
    // Totals from three independent aggregations must agree. If they don't,
    // SOMETHING in the loop above silently dropped units — log loudly so
    // mismatches are visible the moment they happen.
    const totalFromChannels = Object.values(rtvByChannel).reduce((a, b) => a + b, 0);
    let totalFromSkus = 0;
    rtvBySku.forEach(v => { totalFromSkus += v; });
    const totalsAgree = (rtvTotalUnits === totalFromChannels) && (totalFromChannels === totalFromSkus);
    console.log('[recon] RTV validation', {
      dateRange: { startDate, endDate },
      'RTV Total': rtvTotalUnits,
      'RTV by Channel': rtvByChannel,
      'RTV by SKU (top 10)': [...rtvBySku.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10),
      crossCheck: {
        totalFromUnits: rtvTotalUnits,
        totalFromChannels,
        totalFromSkus,
        agree: totalsAgree,
      },
      perSource: rtvPerSource,
    });
    if (!totalsAgree) {
      console.warn('[recon] RTV MISMATCH — total vs channel-sum vs sku-sum disagree', {
        totalFromUnits: rtvTotalUnits, totalFromChannels, totalFromSkus,
      });
    }
    // Checkpoint: FK Mins for Apr'26 ≈ 1,903.
    const fkApr = rtvPerSource.find(s => s.key === 'rtv_fk_mins')?.units || 0;
    if (startDate <= '2026-04-30' && endDate >= '2026-04-01') {
      console.log(`[recon] checkpoint · FK Mins RTV in range ${startDate}…${endDate}: ${fkApr} units (expected ~1903 for Apr'26 only window)`);
    }

    // ============ Accumulate GRN per SKU (date-range filtered) =============
    // GRN INHOUSE has one row per (date × SKU). `TITLE` is the canonical SKU
    // identifier — resolved via the same `titleByKey` Mappings logic so the
    // SKU master/Mappings drives the brand association. `Grn Passed Qty` is
    // the inflow that participates in Ideal Closing. Date-range filter is
    // applied BEFORE aggregation so the GRN card / total / per-SKU row all
    // honour the selected window.
    const grnStats = {
      totalRows: 0, kept: 0, units: 0,
      skippedDateUnparseable: 0, skippedOutOfRange: 0,
      skippedNoId: 0, skippedNoUnits: 0,
      skippedUnmapped: 0, skippedFilteredOut: 0,
      missingCols: false,
    };
    const grnBySku   = new Map();
    const grnByBrand = new Map();
    const grnByDate  = new Map();
    // Track unmapped TITLE values so ops can fix the Mappings sheet. We dedup
    // and keep a sample with their carried units so the log shows the worst
    // offenders first.
    const grnUnmappedTitles = new Map(); // norm(title) → { title, units, rows }
    let grnTotalUnits = 0;
    if (grnRows && grnRows.length > 1) {
      const ghdr = grnRows[0];
      const dateIdx  = findColumnIndex(ghdr, ['grn date']);
      const idIdx    = findColumnIndex(ghdr, ['title']);
      const unitsIdx = findColumnIndex(ghdr, ['grn passed qty', 'grn passed quantity']);
      if (dateIdx === -1 || idIdx === -1 || unitsIdx === -1) {
        grnStats.missingCols = true;
        console.warn(`[recon] GRN INHOUSE missing column(s) → date:${dateIdx} title:${idIdx} units:${unitsIdx}`);
      } else {
        grnRows.slice(1).forEach(row => {
          grnStats.totalRows += 1;
          const d = parseAnyDate(row[dateIdx]);
          if (!d) { grnStats.skippedDateUnparseable += 1; return; }
          if (d < start || d > end) { grnStats.skippedOutOfRange += 1; return; }
          const id = row[idIdx];
          const k = norm(id);
          if (!k) { grnStats.skippedNoId += 1; return; }
          const units = parseNumber(row[unitsIdx]);
          if (!units) { grnStats.skippedNoUnits += 1; return; }
          const title = titleByKey[k] || (inventorySkuSet.has(k) ? k : null);
          if (!title) {
            grnStats.skippedUnmapped += 1;
            const raw = (id || '').toString().trim();
            const slot = grnUnmappedTitles.get(k) || { title: raw, units: 0, rows: 0 };
            slot.units += units;
            slot.rows  += 1;
            grnUnmappedTitles.set(k, slot);
            return;
          }
          const target = rowsByTitle.get(title);
          if (!target) { grnStats.skippedFilteredOut += 1; return; }
          target.grn += units;
          grnBySku.set(title, (grnBySku.get(title) || 0) + units);
          const brand = target.brand || '(unknown)';
          grnByBrand.set(brand, (grnByBrand.get(brand) || 0) + units);
          const dateKey = toISO(d);
          grnByDate.set(dateKey, (grnByDate.get(dateKey) || 0) + units);
          grnStats.kept += 1;
          grnStats.units += units;
          grnTotalUnits += units;
        });
      }
    } else {
      grnStats.missingCols = !grnRows || grnRows.length === 0;
    }

    // ---- GRN validation log + cross-check ----
    let totalFromSkuMap = 0;
    grnBySku.forEach(v => { totalFromSkuMap += v; });
    let totalFromBrandMap = 0;
    grnByBrand.forEach(v => { totalFromBrandMap += v; });
    let totalFromDateMap = 0;
    grnByDate.forEach(v => { totalFromDateMap += v; });
    const grnAgree = (grnTotalUnits === totalFromSkuMap) &&
                     (totalFromSkuMap === totalFromBrandMap) &&
                     (totalFromBrandMap === totalFromDateMap);
    // Sort unmapped TITLEs by units desc so the log surface the rows that hurt
    // Ideal Closing the most — these are the ones worth fixing in Mappings.
    const unmappedSorted = [...grnUnmappedTitles.values()].sort((a, b) => b.units - a.units);
    console.log('[recon] GRN validation', {
      dateRange: { startDate, endDate },
      'GRN Total Units': grnTotalUnits,
      'GRN SKU Count':   grnBySku.size,
      'Unmapped TITLE Count': grnUnmappedTitles.size,
      'GRN by Brand': Object.fromEntries(grnByBrand),
      'GRN by SKU (top 10)': [...grnBySku.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10),
      'GRN by Date (sample 5)': [...grnByDate.entries()].sort().slice(0, 5),
      'Unmapped TITLE Values (top 20 by units)': unmappedSorted.slice(0, 20).map(r => ({
        title: r.title, units: r.units, rows: r.rows,
      })),
      crossCheck: {
        totalUnits: grnTotalUnits, fromSku: totalFromSkuMap,
        fromBrand: totalFromBrandMap, fromDate: totalFromDateMap, agree: grnAgree,
      },
      stats: grnStats,
    });
    if (grnUnmappedTitles.size > 0) {
      console.warn(`[recon] GRN unmapped TITLEs: ${grnUnmappedTitles.size} distinct (${unmappedSorted.reduce((s, r) => s + r.units, 0)} units total) — see "Unmapped TITLE Values" in the GRN validation log above to fix in Mappings sheet`);
    }
    if (!grnAgree) {
      console.warn('[recon] GRN MISMATCH — units / sku / brand / date totals disagree', {
        totalUnits: grnTotalUnits, fromSku: totalFromSkuMap,
        fromBrand: totalFromBrandMap, fromDate: totalFromDateMap,
      });
    }

    // ============ Accumulate F2 → F1 per SKU (date-range filtered) =========
    // "RTO to F01 from APRIL 2025" ledger: one row per F2 → F1 stock
    // movement. Columns: SENT DATE / ASIN / SENT UNIT. ASIN drives SKU
    // resolution through the same Mappings layer that powers Bulk Dispatch
    // and RTV. Brand falls out of the SKU master automatically via the
    // row-seeding pass. Units subtract from Ideal Closing (the formula
    // engine in `recon-config.js` treats f2ToF1 as a negative term).
    const f2Stats = {
      totalRows: 0, kept: 0, units: 0,
      skippedDateUnparseable: 0, skippedOutOfRange: 0,
      skippedNoId: 0, skippedNoUnits: 0,
      skippedUnmapped: 0, skippedFilteredOut: 0,
      missingCols: false,
    };
    const f2BySku   = new Map();
    const f2ByBrand = new Map();
    const f2ByDate  = new Map();
    const f2UnmappedAsins = new Map(); // norm(asin) → { asin, units, rows }
    let f2TotalUnits = 0;
    if (f2ToF1Rows && f2ToF1Rows.length > 1) {
      const fhdr = f2ToF1Rows[0];
      const dateIdx  = findColumnIndex(fhdr, ['sent date']);
      const idIdx    = findColumnIndex(fhdr, ['asin']);
      const unitsIdx = findColumnIndex(fhdr, ['sent unit', 'sent units']);
      if (dateIdx === -1 || idIdx === -1 || unitsIdx === -1) {
        f2Stats.missingCols = true;
        console.warn(`[recon] F2→F1 source missing column(s) → date:${dateIdx} asin:${idIdx} units:${unitsIdx}`);
      } else {
        f2ToF1Rows.slice(1).forEach(row => {
          f2Stats.totalRows += 1;
          const d = parseAnyDate(row[dateIdx]);
          if (!d) { f2Stats.skippedDateUnparseable += 1; return; }
          if (d < start || d > end) { f2Stats.skippedOutOfRange += 1; return; }
          const id = row[idIdx];
          const k = norm(id);
          if (!k) { f2Stats.skippedNoId += 1; return; }
          const units = parseNumber(row[unitsIdx]);
          if (!units) { f2Stats.skippedNoUnits += 1; return; }
          const title = titleByKey[k] || (inventorySkuSet.has(k) ? k : null);
          if (!title) {
            f2Stats.skippedUnmapped += 1;
            const raw = (id || '').toString().trim();
            const slot = f2UnmappedAsins.get(k) || { asin: raw, units: 0, rows: 0 };
            slot.units += units;
            slot.rows  += 1;
            f2UnmappedAsins.set(k, slot);
            return;
          }
          const target = rowsByTitle.get(title);
          if (!target) { f2Stats.skippedFilteredOut += 1; return; }
          target.f2ToF1 += units;
          f2BySku.set(title, (f2BySku.get(title) || 0) + units);
          const brand = target.brand || '(unknown)';
          f2ByBrand.set(brand, (f2ByBrand.get(brand) || 0) + units);
          const dateKey = toISO(d);
          f2ByDate.set(dateKey, (f2ByDate.get(dateKey) || 0) + units);
          f2Stats.kept += 1;
          f2Stats.units += units;
          f2TotalUnits += units;
        });
      }
    } else {
      f2Stats.missingCols = !f2ToF1Rows || f2ToF1Rows.length === 0;
    }

    // ---- F2 → F1 validation log ----
    let f2FromSku = 0;   f2BySku.forEach(v => { f2FromSku += v; });
    let f2FromBrand = 0; f2ByBrand.forEach(v => { f2FromBrand += v; });
    let f2FromDate = 0;  f2ByDate.forEach(v => { f2FromDate += v; });
    const f2Agree = (f2TotalUnits === f2FromSku) &&
                    (f2FromSku === f2FromBrand) &&
                    (f2FromBrand === f2FromDate);
    const f2UnmappedSorted = [...f2UnmappedAsins.values()].sort((a, b) => b.units - a.units);
    console.log('[recon] F2→F1 validation', {
      dateRange: { startDate, endDate },
      'F2→F1 Total Units': f2TotalUnits,
      'F2→F1 SKU Count':   f2BySku.size,
      'Unmapped ASIN Count': f2UnmappedAsins.size,
      'F2→F1 by Brand': Object.fromEntries(f2ByBrand),
      'F2→F1 by SKU (top 10)': [...f2BySku.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10),
      'F2→F1 by Date (sample 5)': [...f2ByDate.entries()].sort().slice(0, 5),
      'Unmapped ASIN Values (top 20 by units)': f2UnmappedSorted.slice(0, 20).map(r => ({
        asin: r.asin, units: r.units, rows: r.rows,
      })),
      crossCheck: {
        totalUnits: f2TotalUnits, fromSku: f2FromSku,
        fromBrand: f2FromBrand, fromDate: f2FromDate, agree: f2Agree,
      },
      stats: f2Stats,
    });
    if (f2UnmappedAsins.size > 0) {
      console.warn(`[recon] F2→F1 unmapped ASINs: ${f2UnmappedAsins.size} distinct (${f2UnmappedSorted.reduce((s, r) => s + r.units, 0)} units total) — see "Unmapped ASIN Values" in the F2→F1 validation log above to fix in Mappings sheet`);
    }
    if (!f2Agree) {
      console.warn('[recon] F2→F1 MISMATCH — units / sku / brand / date totals disagree', {
        totalUnits: f2TotalUnits, fromSku: f2FromSku,
        fromBrand: f2FromBrand, fromDate: f2FromDate,
      });
    }

    // ============ DSR — Opening & Closing per SKU ===========================
    // Layout: ASIN | Brand | DRR | DOH | Title | Type | <DD/MM/YYYY headers...>
    //   • Opening Stock = inventory on (invStartDate − 1)
    //   • Closing Stock = inventory on (invEndDate   − 1)
    // The user spec'd D-1: "Inventory Range 02-Jun → 06-Jun ⇒ Opening = 01-Jun,
    // Closing = 05-Jun". If a D-1 column is absent, the freshest column ≤ D-1
    // is used and a console warning logged. Brand falls out of the SKU master.
    //
    // NOTE: The inventory window is INDEPENDENT of the transaction window —
    // operations can compare a long inventory swing (e.g., Jan opening vs Jun
    // closing) against a tight transaction slice (e.g., just June flows) to
    // surface variance against any reference snapshot pair.
    const dsrStats = {
      totalRows: 0, kept: 0, missingTitle: 0, unmapped: 0, filteredOut: 0,
      openingDateUsed: null, closingDateUsed: null,
      openingDateRequested: null, closingDateRequested: null,
      missingHeader: false,
    };
    const openingBySku = new Map();
    const closingBySku = new Map();
    const minusOne = (iso) => {
      // ISO YYYY-MM-DD → string for the previous day, parsed locally.
      if (!iso) return null;
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return null;
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      d.setDate(d.getDate() - 1);
      return toISO(d);
    };
    const requestedOpening = minusOne(invStartDate);
    const requestedClosing = minusOne(invEndDate);
    dsrStats.openingDateRequested = requestedOpening;
    dsrStats.closingDateRequested = requestedClosing;

    let openingTotal = 0;
    let closingTotal = 0;

    if (dsrRows && dsrRows.length > 1 && requestedOpening && requestedClosing) {
      const dhdr = dsrRows[0];
      const titleIdx = findColumnIndex(dhdr, ['title']);
      const typeIdx  = findColumnIndex(dhdr, ['type']);
      if (titleIdx === -1 || typeIdx === -1) {
        dsrStats.missingHeader = true;
        console.warn('[recon] DSR missing Title/Type column → title:%d type:%d', titleIdx, typeIdx);
      } else {
        // Build ISO → column-index lookup for every date header AFTER `Type`.
        // Headers are DD/MM/YYYY in the source sheet.
        const dateIdxByIso = new Map();
        const sortedIsoCols = []; // ISO date strings in ascending order
        for (let i = typeIdx + 1; i < dhdr.length; i += 1) {
          const cell = (dhdr[i] || '').toString().trim();
          if (!cell) continue;
          const dt = parseAnyDate(cell);
          if (!dt) continue;
          const iso = toISO(dt);
          dateIdxByIso.set(iso, i);
          sortedIsoCols.push(iso);
        }
        sortedIsoCols.sort();

        // Resolve a requested date to the freshest column ≤ that date.
        const resolveColIdx = (wanted) => {
          if (dateIdxByIso.has(wanted)) return { iso: wanted, idx: dateIdxByIso.get(wanted), exact: true };
          // Walk backwards through the sorted column list.
          for (let i = sortedIsoCols.length - 1; i >= 0; i -= 1) {
            if (sortedIsoCols[i] <= wanted) {
              return { iso: sortedIsoCols[i], idx: dateIdxByIso.get(sortedIsoCols[i]), exact: false };
            }
          }
          return null;
        };
        const openingCol = resolveColIdx(requestedOpening);
        const closingCol = resolveColIdx(requestedClosing);
        dsrStats.openingDateUsed = openingCol?.iso || null;
        dsrStats.closingDateUsed = closingCol?.iso || null;

        if (!openingCol) console.warn(`[recon] DSR has no column ≤ requested Opening date ${requestedOpening}`);
        if (!closingCol) console.warn(`[recon] DSR has no column ≤ requested Closing date ${requestedClosing}`);

        dsrRows.slice(1).forEach(row => {
          dsrStats.totalRows += 1;
          // Type rule: drop rows whose Type is one of the 4 variant rows
          // (New MRP / Old MRP / New Label / Old Label) — the SKU's `Total`
          // row already aggregates them. Rows with blank Type (no variants)
          // are kept as-is.
          const typeVal = (row[typeIdx] || '').toString().trim();
          if (isReconDropType(typeVal)) {
            dsrStats.filteredOut += 1;
            return;
          }
          const rawTitle = (row[titleIdx] || '').toString().trim();
          if (!rawTitle) { dsrStats.missingTitle += 1; return; }
          const k = norm(rawTitle);
          const title = titleByKey[k] || (inventorySkuSet.has(k) ? k : null);
          if (!title) { dsrStats.unmapped += 1; return; }
          const target = rowsByTitle.get(title);
          if (!target) { dsrStats.filteredOut += 1; return; }

          const op = openingCol ? parseNumber(row[openingCol.idx]) : 0;
          const cl = closingCol ? parseNumber(row[closingCol.idx]) : 0;
          target.openingStock += op;
          target.closingStock += cl;
          openingBySku.set(title, (openingBySku.get(title) || 0) + op);
          closingBySku.set(title, (closingBySku.get(title) || 0) + cl);
          openingTotal += op;
          closingTotal += cl;
          dsrStats.kept += 1;
        });
      }
    }

    // ---- DSR validation log ----
    let openFromSku = 0;  openingBySku.forEach(v => { openFromSku += v; });
    let closeFromSku = 0; closingBySku.forEach(v => { closeFromSku += v; });
    const dsrAgree = (openingTotal === openFromSku) && (closingTotal === closeFromSku);
    console.log('[recon] DSR validation', {
      dateRange: { invStartDate, invEndDate },
      'Opening date requested (start−1)': requestedOpening,
      'Opening date used':                dsrStats.openingDateUsed,
      'Closing date requested (end−1)':   requestedClosing,
      'Closing date used':                dsrStats.closingDateUsed,
      'Opening Stock Total':              openingTotal,
      'Closing Stock Total':              closingTotal,
      'Opening SKU Count':                openingBySku.size,
      'Closing SKU Count':                closingBySku.size,
      crossCheck: { openingTotal, openFromSku, closingTotal, closeFromSku, agree: dsrAgree },
      stats: dsrStats,
    });
    if (!dsrAgree) {
      console.warn('[recon] DSR MISMATCH — Total vs SKU-map sums disagree', {
        openingTotal, openFromSku, closingTotal, closeFromSku,
      });
    }

    // ============ Accumulate RTO from Consumer Returns (date-range filtered) ===
    // Sources: five tabs share the same shape, processed sequentially:
    //   • 'current'  → 'Consumer Returns (June 2026 onwards)' — primary feed.
    //   • 'apr2026'  → 'RTO Apr'26'  — backfill for April 2026 ONLY.
    //   • 'may2026'  → 'RTO May'26'  — backfill for May 2026 ONLY.
    //   • 'jun2026'  → 'RTO June'26' — backfill for June 2026 ONLY.
    //   • 'jul2026'  → 'RTO July'26' — backfill for July 2026 ONLY.
    // Mutually-exclusive month mask:
    //   • April 2026 rows are accepted ONLY from `apr2026`.
    //   • May   2026 rows are accepted ONLY from `may2026`.
    //   • June  2026 rows are accepted ONLY from `jun2026`.
    //   • July  2026 rows are accepted ONLY from `jul2026`.
    //   • Every other date is accepted ONLY from `current`.
    // All feeds are then merged into the same byChannel / bySku / byBrand /
    // byDate buckets so the cards / sub-table / SKU table see one
    // reconciled total. Each feed gets its own per-source validation log
    // (rows, source raw, mapped, unmapped, filtered) so ops can spot
    // discrepancies in any tab in isolation.
    //
    // Columns by tab:
    //   Consumer Returns (Jul+) : Date | CHANNEL | SKU 1..4 | QUANTITY  (+ ASIN/SKU fallback cols)
    //   RTO Apr'26              : Date | SKU | Channel | SKU Name | SKU 1..4 | QUANTITY
    //   RTO May'26              : Date | CHANNEL | SKU | SKU Name | SKU 1..4 | QUANTITY
    //   RTO June'26             : Date | CHANNEL | SKU Name | SKU 1..4 | QUANTITY
    //   RTO July'26             : Date | CHANNEL | SKU Name | SKU 1..4 | QUANTITY
    // findColumnIndex is case/punctuation tolerant so all shapes resolve.
    //
    // Multi-SKU attribution (mirrors MTD Sales 1 in Projections vs Consumption):
    //   • 1 SKU populated   → full QUANTITY → that SKU.
    //   • multiple SKUs     → full QUANTITY → EACH listed SKU (NOT split).
    const rtoStats = {
      totalRows: 0, kept: 0, units: 0,
      skippedDateUnparseable: 0, skippedOutOfRange: 0,
      skippedNoSku: 0, skippedNoUnits: 0,
      skippedUnmapped: 0, skippedFilteredOut: 0,
      missingCols: false,
      perSource: {},
    };
    const rtoBySku     = new Map();
    const rtoByChannel = new Map();
    const rtoByDate    = new Map();
    const rtoByBrand   = new Map();
    const rtoUnmappedChannels = new Set();
    const rtoUnmappedSkus = new Map(); // norm(sku) → { sku, units, rows }
    let rtoTotalUnits = 0;
    // Cross-source reconciliation accumulators.
    let totalSourceRawTotal = 0, totalSourceFullAttrTotal = 0;
    let totalMappedUnits = 0, totalUnmappedUnits = 0, totalFilteredOutUnits = 0;
    let totalViaSku14 = 0, totalViaAsin = 0, totalViaSkuCol = 0;

    // Mutually-exclusive month mask. Each row sits in exactly one feed:
    //   • April 2026 → only from `apr2026`
    //   • May   2026 → only from `may2026`
    //   • June  2026 → only from `jun2026`
    //   • July  2026 → only from `jul2026`
    //   • everything else (incl. Aug'26+ ongoing) → only from `current`
    // Adding another month-specific backfill is one new isXxxRow + one
    // entry in `rtoSourceList`.
    const isAprilRow = (d) =>
      d && d.getFullYear() === 2026 && d.getMonth() === 3; // 0-indexed: 3 = Apr
    const isMayRow = (d) =>
      d && d.getFullYear() === 2026 && d.getMonth() === 4; // 0-indexed: 4 = May
    const isJuneRow = (d) =>
      d && d.getFullYear() === 2026 && d.getMonth() === 5; // 0-indexed: 5 = Jun
    const isJulyRow = (d) =>
      d && d.getFullYear() === 2026 && d.getMonth() === 6; // 0-indexed: 6 = Jul

    const rtoSourceList = [
      { key: 'current', rows: rtoSources?.current?.rows || [],
        accept: (d) => !isAprilRow(d) && !isMayRow(d) && !isJuneRow(d) && !isJulyRow(d) },
      { key: 'apr2026', rows: rtoSources?.apr2026?.rows || [],
        accept: (d) =>  isAprilRow(d) },
      { key: 'may2026', rows: rtoSources?.may2026?.rows || [],
        accept: (d) =>  isMayRow(d) },
      { key: 'jun2026', rows: rtoSources?.jun2026?.rows || [],
        accept: (d) =>  isJuneRow(d) },
      { key: 'jul2026', rows: rtoSources?.jul2026?.rows || [],
        accept: (d) =>  isJulyRow(d) },
    ];

    rtoSourceList.forEach(({ key: srcKey, rows: srcRows, accept }) => {
      const perSrc = {
        rows: 0, accepted: 0,
        skippedDateUnparseable: 0, skippedMonthMask: 0,
        skippedOutOfRange: 0, skippedNoUnits: 0,
        skippedNoSku: 0, skippedFilteredOut: 0, skippedUnmapped: 0,
        sourceRawTotal: 0, sourceFullAttrTotal: 0,
        mappedUnits: 0, unmappedUnits: 0, filteredOutUnits: 0,
        viaSku14: 0, viaAsin: 0, viaSkuCol: 0,
        missingCols: false,
      };
      rtoStats.perSource[srcKey] = perSrc;
      if (!srcRows || srcRows.length <= 1) return;

      const rhdr = srcRows[0];
      const dateIdx  = findColumnIndex(rhdr, ['date']);
      const chanIdx  = findColumnIndex(rhdr, ['channel']);
      const qtyIdx   = findColumnIndex(rhdr, ['quantity']);
      // Fallback identifier columns: when a title in SKU 1..4 doesn't resolve
      // via Mappings, the row's ASIN or canonical SKU column often does.
      const asinIdx  = findColumnIndex(rhdr, ['asin']);
      const skuColIdx = findColumnIndex(rhdr, ['sku']);
      const skuIdxs  = [
        findColumnIndex(rhdr, ['sku 1']),
        findColumnIndex(rhdr, ['sku 2']),
        findColumnIndex(rhdr, ['sku 3']),
        findColumnIndex(rhdr, ['sku 4']),
      ].filter(i => i !== -1);
      if (dateIdx === -1 || chanIdx === -1 || qtyIdx === -1 || skuIdxs.length === 0) {
        perSrc.missingCols = true;
        rtoStats.missingCols = true;
        console.warn(`[recon] RTO source [${srcKey}] missing column(s) → date:${dateIdx} channel:${chanIdx} qty:${qtyIdx} sku-cols:${skuIdxs.length}`);
        return;
      }

      srcRows.slice(1).forEach(row => {
        perSrc.rows += 1;
        rtoStats.totalRows += 1;
        const d = parseAnyDate(row[dateIdx]);
        if (!d) { perSrc.skippedDateUnparseable += 1; rtoStats.skippedDateUnparseable += 1; return; }
        // Mutually-exclusive month mask — every row sits in exactly one feed.
        if (!accept(d)) { perSrc.skippedMonthMask += 1; return; }
        if (d < start || d > end) { perSrc.skippedOutOfRange += 1; rtoStats.skippedOutOfRange += 1; return; }
        const qty = parseNumber(row[qtyIdx]);
        if (!qty) { perSrc.skippedNoUnits += 1; rtoStats.skippedNoUnits += 1; return; }
        perSrc.sourceRawTotal += qty;
        totalSourceRawTotal   += qty;
        // Channel: map raw → canonical (case-insensitive on the keys).
        const rawChan = (row[chanIdx] || '').toString().trim();
        const mapped  = RTO_CHANNEL_MAP[rawChan.toLowerCase()] || rawChan;
        if (!RTO_CHANNEL_MAP[rawChan.toLowerCase()] && rawChan) {
          rtoUnmappedChannels.add(rawChan);
        }
        // Resolve each SKU position to a canonical title.
        const skus = [];
        const seen = new Set();
        skuIdxs.forEach(si => {
          const v = (row[si] || '').toString().trim();
          if (!v) return;
          const nk = norm(v);
          if (seen.has(nk)) return;
          seen.add(nk);
          skus.push(v);
        });
        if (skus.length === 0) {
          // No SKU 1..4 — try ASIN then SKU column.
          const asin = asinIdx !== -1 ? (row[asinIdx] || '').toString().trim() : '';
          const skuCol = skuColIdx !== -1 ? (row[skuColIdx] || '').toString().trim() : '';
          if (asin) skus.push(asin);
          else if (skuCol) skus.push(skuCol);
        }
        if (skus.length === 0) {
          perSrc.skippedNoSku += 1; rtoStats.skippedNoSku += 1;
          perSrc.unmappedUnits += qty; totalUnmappedUnits += qty;
          return;
        }
        perSrc.sourceFullAttrTotal  += qty * skus.length;
        totalSourceFullAttrTotal    += qty * skus.length;
        // Resolution per SKU position with fallback chain.
        const asinRaw = asinIdx !== -1 ? (row[asinIdx] || '').toString().trim() : '';
        const skuColRaw = skuColIdx !== -1 ? (row[skuColIdx] || '').toString().trim() : '';
        let anyApplied = false;
        skus.forEach(skuVal => {
          const k = norm(skuVal);
          let title = titleByKey[k] || (inventorySkuSet.has(k) ? k : null);
          let how = 'sku14';
          if (!title && asinRaw) {
            const ak = norm(asinRaw);
            title = titleByKey[ak] || (inventorySkuSet.has(ak) ? ak : null);
            if (title) how = 'asin';
          }
          if (!title && skuColRaw) {
            const sk = norm(skuColRaw);
            title = titleByKey[sk] || (inventorySkuSet.has(sk) ? sk : null);
            if (title) how = 'skuCol';
          }
          if (!title) {
            const slot = rtoUnmappedSkus.get(k) || { sku: skuVal, units: 0, rows: 0, src: srcKey };
            slot.units += qty; slot.rows += 1;
            rtoUnmappedSkus.set(k, slot);
            perSrc.unmappedUnits += qty; totalUnmappedUnits += qty;
            return;
          }
          const target = rowsByTitle.get(title);
          if (!target) {
            // Resolved but excluded by global brand/SKU/shaker filters.
            perSrc.skippedFilteredOut += 1; rtoStats.skippedFilteredOut += 1;
            perSrc.filteredOutUnits += qty; totalFilteredOutUnits += qty;
            return;
          }
          if (!(mapped in target.rto.byChannel)) target.rto.byChannel[mapped] = 0;
          target.rto.byChannel[mapped] += qty;
          target.rto.total += qty;
          rtoBySku.set(title, (rtoBySku.get(title) || 0) + qty);
          rtoByChannel.set(mapped, (rtoByChannel.get(mapped) || 0) + qty);
          rtoByBrand.set(target.brand || '(unknown)', (rtoByBrand.get(target.brand || '(unknown)') || 0) + qty);
          const dateKey = toISO(d);
          rtoByDate.set(dateKey, (rtoByDate.get(dateKey) || 0) + qty);
          rtoTotalUnits += qty;
          perSrc.mappedUnits += qty; totalMappedUnits += qty;
          anyApplied = true;
          if (how === 'sku14')      { perSrc.viaSku14  += qty; totalViaSku14  += qty; }
          else if (how === 'asin')  { perSrc.viaAsin   += qty; totalViaAsin   += qty; }
          else if (how === 'skuCol'){ perSrc.viaSkuCol += qty; totalViaSkuCol += qty; }
        });
        if (anyApplied) { perSrc.accepted += 1; rtoStats.kept += 1; }
        else            { perSrc.skippedUnmapped += 1; rtoStats.skippedUnmapped += 1; }
      });
    });

    // Stash cross-source reconciliation snapshot for the validation log.
    rtoStats.sourceRawTotal      = totalSourceRawTotal;
    rtoStats.sourceFullAttrTotal = totalSourceFullAttrTotal;
    rtoStats.mappedUnits         = totalMappedUnits;
    rtoStats.unmappedUnits       = totalUnmappedUnits;
    rtoStats.filteredOutUnits    = totalFilteredOutUnits;
    rtoStats.resolvedViaSku14    = totalViaSku14;
    rtoStats.resolvedViaAsin     = totalViaAsin;
    rtoStats.resolvedViaSkuCol   = totalViaSkuCol;

    // ---- RTO validation log ----
    let rtoFromSku = 0;     rtoBySku.forEach(v => { rtoFromSku += v; });
    let rtoFromChannel = 0; rtoByChannel.forEach(v => { rtoFromChannel += v; });
    let rtoFromBrand = 0;   rtoByBrand.forEach(v => { rtoFromBrand += v; });
    let rtoFromDate = 0;    rtoByDate.forEach(v => { rtoFromDate += v; });
    const rtoAgree =
      (rtoTotalUnits === rtoFromSku) &&
      (rtoFromSku === rtoFromChannel) &&
      (rtoFromChannel === rtoFromBrand) &&
      (rtoFromBrand === rtoFromDate);
    const rtoUnmappedSorted = [...rtoUnmappedSkus.values()].sort((a, b) => b.units - a.units);
    console.log('[recon] RTO validation', {
      dateRange: { startDate, endDate },
      'RTO Total Units':   rtoTotalUnits,
      'RTO SKU Count':     rtoBySku.size,
      'RTO by Channel':    Object.fromEntries(rtoByChannel),
      'RTO by Brand':      Object.fromEntries(rtoByBrand),
      'RTO by SKU (top 10)': [...rtoBySku.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10),
      'Unmapped SKU Count':      rtoUnmappedSkus.size,
      'Unmapped SKU Values (top 20)': rtoUnmappedSorted.slice(0, 20),
      'Unmapped Channel Values':      [...rtoUnmappedChannels],
      // Per-source breakdown — every QTY unit is accepted by exactly one
      // feed thanks to the April-month mask. `monthMasked` is the count of
      // rows skipped because they belong to the OTHER feed's month window.
      perSource: Object.fromEntries(
        Object.entries(rtoStats.perSource).map(([k, s]) => [k, {
          rows: s.rows, accepted: s.accepted,
          monthMasked: s.skippedMonthMask, outOfRange: s.skippedOutOfRange,
          sourceRawTotal: s.sourceRawTotal, mapped: s.mappedUnits,
          unmapped: s.unmappedUnits, filteredOut: s.filteredOutUnits,
        }])
      ),
      // Source-vs-page reconciliation. `sourceRawTotal` is one QTY per row
      // (the raw sheet aggregate); `sourceFullAttrTotal` multiplies QTY by
      // the number of distinct SKUs in each row (full attribution).
      // Bucket math: sourceFullAttrTotal = mapped + unmapped + filteredOut.
      reconciliation: {
        sourceRawTotal:      rtoStats.sourceRawTotal,
        sourceFullAttrTotal: rtoStats.sourceFullAttrTotal,
        mappedUnits:         rtoStats.mappedUnits,
        unmappedUnits:       rtoStats.unmappedUnits,
        filteredOutUnits:    rtoStats.filteredOutUnits,
        bucketSum:           (rtoStats.mappedUnits || 0) + (rtoStats.unmappedUnits || 0) + (rtoStats.filteredOutUnits || 0),
        resolution: {
          viaSku14:  rtoStats.resolvedViaSku14  || 0,
          viaAsin:   rtoStats.resolvedViaAsin   || 0,
          viaSkuCol: rtoStats.resolvedViaSkuCol || 0,
        },
      },
      crossCheck: { totalUnits: rtoTotalUnits, fromSku: rtoFromSku, fromChannel: rtoFromChannel, fromBrand: rtoFromBrand, fromDate: rtoFromDate, agree: rtoAgree },
      stats: rtoStats,
    });
    if (rtoUnmappedSkus.size > 0) {
      console.warn(`[recon] RTO unmapped SKUs: ${rtoUnmappedSkus.size} distinct (${rtoUnmappedSorted.reduce((s, r) => s + r.units, 0)} units total) — see "Unmapped SKU Values" in the RTO validation log above to fix in Mappings sheet`);
    }
    if (rtoUnmappedChannels.size > 0) {
      console.warn(`[recon] RTO unmapped channels: ${[...rtoUnmappedChannels].join(', ')} — add to RTO_CHANNEL_MAP if these should map to canonical names`);
    }
    if (!rtoAgree) {
      console.warn('[recon] RTO MISMATCH — totals disagree', {
        totalUnits: rtoTotalUnits, fromSku: rtoFromSku, fromChannel: rtoFromChannel,
        fromBrand: rtoFromBrand, fromDate: rtoFromDate,
      });
    }


    // ============ Accumulate B2C Orders per (SKU × channel) ================
    // Per-channel pivot tabs: Title | SKU 1..4 | <date headers DD/MM/YYYY ...>
    // Each cell value at row × date-column = units sold that day for that
    // SKU-group. Channel is implicit (one tab per channel — same pattern as
    // RTV sources). For every row we resolve the SKU-group via SKU 1..4 with
    // multi-SKU FULL ATTRIBUTION (full units to EACH listed SKU, identical
    // to MTD Sales / RTO). Each cell only contributes when its date header
    // falls inside the selected range.
    const b2cStats = {
      perSource: [],
      totalUnits: 0,
      unmappedSkus: new Map(),  // norm(sku) → { sku, units, rows }
    };
    B2C_SOURCES.forEach(src => {
      const sheet = b2cData[src.key];
      const ps = {
        key: src.key, tab: src.tab, channel: src.channel,
        totalRows: 0, kept: 0, units: 0,
        skippedNoSku: 0, skippedNoUnits: 0, skippedUnmapped: 0, skippedFilteredOut: 0,
        dateColsInRange: 0, dateColsTotal: 0, missingCols: false,
      };
      b2cStats.perSource.push(ps);
      if (!sheet || !sheet.rows || sheet.rows.length < 2) {
        ps.missingCols = !sheet;
        return;
      }
      const hdr = sheet.rows[0];
      const titleIdx = findColumnIndex(hdr, ['title']);
      const skuIdxs = [
        findColumnIndex(hdr, ['sku 1']),
        findColumnIndex(hdr, ['sku 2']),
        findColumnIndex(hdr, ['sku 3']),
        findColumnIndex(hdr, ['sku 4']),
      ].filter(i => i !== -1);
      if (titleIdx === -1 || skuIdxs.length === 0) {
        ps.missingCols = true;
        console.warn(`[recon] B2C "${src.tab}" missing Title or SKU 1..4 columns`);
        return;
      }
      // Build the in-range date column list once per source.
      const dateColIdxs = []; // [{ idx, iso }] for every header column whose date is in window
      for (let i = 0; i < hdr.length; i += 1) {
        if (i === titleIdx || skuIdxs.includes(i)) continue;
        const v = (hdr[i] || '').toString().trim();
        if (!v) continue;
        const dt = parseAnyDate(v);
        if (!dt) continue;
        ps.dateColsTotal += 1;
        if (dt < start || dt > end) continue;
        ps.dateColsInRange += 1;
        dateColIdxs.push({ idx: i, iso: toISO(dt) });
      }
      if (dateColIdxs.length === 0) return; // nothing in window — clean exit

      sheet.rows.slice(1).forEach(row => {
        ps.totalRows += 1;
        // Collect distinct non-blank SKUs across SKU 1..4 (TITLE-style values).
        const skus = [];
        const seen = new Set();
        skuIdxs.forEach(si => {
          const v = (row[si] || '').toString().trim();
          if (!v) return;
          const nk = norm(v);
          if (seen.has(nk)) return;
          seen.add(nk);
          skus.push(v);
        });
        if (skus.length === 0) { ps.skippedNoSku += 1; return; }
        // Resolve each SKU to a known title. Fallback to row's Title column
        // when SKU 1..4 don't resolve (some rows have only Title populated).
        const titleFallback = (row[titleIdx] || '').toString().trim();
        let rowApplied = false;
        skus.forEach(skuVal => {
          const k = norm(skuVal);
          let title = titleByKey[k] || (inventorySkuSet.has(k) ? k : null);
          if (!title && titleFallback) {
            const tk = norm(titleFallback);
            title = titleByKey[tk] || (inventorySkuSet.has(tk) ? tk : null);
          }
          if (!title) {
            // Sum across in-range date cells for this unmapped SKU so the
            // log shows how much inventory is missing from Mappings.
            let unmappedUnits = 0;
            dateColIdxs.forEach(({ idx }) => { unmappedUnits += parseNumber(row[idx]); });
            if (unmappedUnits > 0) {
              const slot = b2cStats.unmappedSkus.get(k) || { sku: skuVal, units: 0, rows: 0 };
              slot.units += unmappedUnits; slot.rows += 1;
              b2cStats.unmappedSkus.set(k, slot);
            }
            ps.skippedUnmapped += 1;
            return;
          }
          const target = rowsByTitle.get(title);
          if (!target) { ps.skippedFilteredOut += 1; return; }
          // Walk every in-range date cell, add units. Full attribution: each
          // mapped SKU receives the FULL cell value (no splitting).
          let rowUnits = 0;
          dateColIdxs.forEach(({ idx }) => {
            const u = parseNumber(row[idx]);
            if (!u) return;
            rowUnits += u;
          });
          if (rowUnits === 0) { ps.skippedNoUnits += 1; return; }
          if (!(src.channel in target.b2cOrders.byChannel)) {
            target.b2cOrders.byChannel[src.channel] = 0;
          }
          target.b2cOrders.byChannel[src.channel] += rowUnits;
          target.b2cOrders.total += rowUnits;
          ps.units += rowUnits;
          b2cStats.totalUnits += rowUnits;
          rowApplied = true;
        });
        if (rowApplied) ps.kept += 1;
      });
    });

    // ---- B2C validation log ----
    const b2cUnmappedSorted = [...b2cStats.unmappedSkus.values()].sort((a, b) => b.units - a.units);
    const b2cByChannel = {};
    let b2cTotalFromRows = 0;
    let b2cSkuSet = new Set();
    rowsByTitle.forEach(r => {
      if (r.b2cOrders.total > 0) b2cSkuSet.add(r.sku);
      Object.entries(r.b2cOrders.byChannel).forEach(([ch, v]) => {
        if (!v) return;
        b2cByChannel[ch] = (b2cByChannel[ch] || 0) + v;
        b2cTotalFromRows += v;
      });
    });
    console.log('[recon] B2C validation', {
      dateRange: { startDate, endDate },
      'B2C Total Units':   b2cStats.totalUnits,
      'B2C SKU Count':     b2cSkuSet.size,
      'B2C by Channel':    b2cByChannel,
      'Unmapped SKU Count': b2cStats.unmappedSkus.size,
      'Unmapped SKU Values (top 20)': b2cUnmappedSorted.slice(0, 20),
      crossCheck: {
        sumFromSources: b2cStats.perSource.reduce((s, p) => s + p.units, 0),
        sumFromRows:    b2cTotalFromRows,
        agree:          b2cStats.totalUnits === b2cTotalFromRows,
      },
      perSource: b2cStats.perSource,
    });
    if (b2cStats.unmappedSkus.size > 0) {
      console.warn(`[recon] B2C unmapped SKUs: ${b2cStats.unmappedSkus.size} distinct (${b2cUnmappedSorted.reduce((s, r) => s + r.units, 0)} units total) — fix in Mappings sheet`);
    }
    if (b2cStats.totalUnits !== b2cTotalFromRows) {
      console.warn('[recon] B2C MISMATCH — sum-from-sources vs sum-from-rows disagree', {
        fromSources: b2cStats.totalUnits, fromRows: b2cTotalFromRows,
      });
    }

    // data sources are wired, accumulate them into the corresponding fields
    // here. Same per-channel pattern for expandable metrics.

    // Run derived (Ideal Closing, Variance) on every row.
    const out = [];
    rowsByTitle.forEach(row => { computeSkuDerived(row); out.push(row); });
    return out;
  }, [overviewDicts, startDate, endDate, invStartDate, invEndDate, selectedBrands, selectedSkus, selectedAsins, dispatchData, rtvData, grnRows, f2ToF1Rows, dsrRows, rtoSources, b2cData, titleByKey, inventorySkuSet]);

  // ============ Sorting ============
  const sortedRows = useMemo(() => {
    const cp = [...skuRows];
    const getVal = (r, key) => {
      if (EXPANDABLE_METRIC_KEYS.includes(key)) return r[key]?.total || 0;
      if (key.startsWith('ch:')) {
        const [, metric, ch] = key.split(':');
        return r[metric]?.byChannel?.[ch] || 0;
      }
      return r[key];
    };
    cp.sort((a, b) => {
      let av = getVal(a, sortKey);
      let bv = getVal(b, sortKey);
      if (av == null) av = '';
      if (bv == null) bv = '';
      if (typeof av === 'string') {
        const sa = av.toLowerCase(); const sb = (bv || '').toString().toLowerCase();
        if (sa < sb) return sortDir === 'asc' ? -1 : 1;
        if (sa > sb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return cp;
  }, [skuRows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // ============ Totals row ============
  const totals = useMemo(() => {
    const base = emptyReconSkuRow('Total', '', '');
    sortedRows.forEach(r => {
      base.openingStock += r.openingStock;
      base.grn          += r.grn;
      base.f2ToF1       += r.f2ToF1;
      base.closingStock += r.closingStock;
      EXPANDABLE_METRIC_KEYS.forEach(mk => {
        base[mk].total += r[mk].total;
        channelsForMetric(mk).forEach(ch => {
          base[mk].byChannel[ch] += r[mk].byChannel[ch] || 0;
        });
      });
    });
    return computeSkuDerived(base);
  }, [sortedRows]);

  // ============ Card metric totals (driven by table, stay consistent) ============
  const metricTotals = useMemo(() => ({
    openingStock: totals.openingStock,
    bulkDispatch: totals.bulkDispatch.total,
    b2cOrders:    totals.b2cOrders.total,
    rto:          totals.rto.total,
    rtv:          totals.rtv.total,
    grn:          totals.grn,
    f2ToF1:       totals.f2ToF1,
    idealClosing: totals.idealClosing,
    closingStock: totals.closingStock,
    variance:     totals.variance,
  }), [totals]);

  // ============ Expand toggle for expandable metric columns ============
  const toggleExpand = (key) => {
    setExpandedCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ============ Excel export ============
  // Mirrors what's currently rendered (respects expand state) so users see
  // the same numbers in the file as on screen.
  const handleDownload = () => {
    if (sortedRows.length === 0) { toast.error('No data to export'); return; }

    // Build header row 1 (group spans) + row 2 (sub-cols where applicable).
    const baseLeftCols = ['ASIN', 'BRAND', 'SKU', 'Opening Stock'];
    const trailingCols = ['GRN', 'F2 → F1', 'Ideal Closing Stock', 'Closing Stock', 'Variance'];

    // Compose flat column list as `{ label, getter }` so we can produce both
    // headers and per-row values in lockstep.
    const cols = [];
    cols.push({ label: 'ASIN',          get: r => r.asin });
    cols.push({ label: 'BRAND',         get: r => r.brand });
    cols.push({ label: 'SKU',           get: r => r.sku });
    cols.push({ label: 'Opening Stock', get: r => r.openingStock });

    EXPANDABLE_METRIC_KEYS.forEach(mk => {
      const meta = RECON_METRICS.find(m => m.key === mk);
      cols.push({ label: meta.label, get: r => r[mk].total });
      if (expandedCols.has(mk)) {
        channelsForMetric(mk).forEach(ch => {
          cols.push({ label: `${meta.label} — ${ch}`, get: r => r[mk].byChannel[ch] || 0 });
        });
      }
    });

    cols.push({ label: 'GRN',                  get: r => r.grn });
    cols.push({ label: 'F2 → F1',              get: r => r.f2ToF1 });
    cols.push({ label: 'Ideal Closing Stock',  get: r => r.idealClosing });
    cols.push({ label: 'Closing Stock',        get: r => r.closingStock });
    cols.push({ label: 'Variance',             get: r => r.variance });

    const header = cols.map(c => c.label);
    const dataRows = sortedRows.map(r => cols.map(c => c.get(r)));
    // Append totals row at bottom.
    const totalsRow = cols.map((c, i) => {
      if (i === 0) return 'Total';
      if (i === 1 || i === 2) return '';
      return c.get(totals);
    });
    const aoa = [header, ...dataRows, totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Numeric formatting starts at "Opening Stock" column.
    for (let R = 1; R < aoa.length; R++) {
      for (let C = 3; C < header.length; C++) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[ref]) { ws[ref].t = 'n'; ws[ref].z = '0'; }
      }
    }
    ws['!cols'] = header.map(h => ({ wch: Math.max(h.length + 2, 12) }));
    ws['!views'] = [{ state: 'frozen', ySplit: 1 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Recon');
    // Summary sheet — cards as a small table.
    const summary = [['Metric', 'Total']];
    RECON_METRICS.forEach(m => summary.push([m.label, metricTotals[m.key] || 0]));
    summary.push(['Transaction Window', `${startDate} → ${endDate}`]);
    summary.push(['Inventory Window',   `${invStartDate} → ${invEndDate}`]);
    if (selectedBrands.length) summary.push(['Brands', selectedBrands.join(', ')]);
    if (selectedAsins.length)  summary.push(['ASINs',  selectedAsins.join(', ')]);
    if (selectedSkus.length)   summary.push(['SKUs',   selectedSkus.join(', ')]);
    const wsSum = XLSX.utils.aoa_to_sheet(summary);
    wsSum['!cols'] = [{ wch: 24 }, { wch: 36 }];
    XLSX.utils.book_append_sheet(wb, wsSum, 'Summary');
    XLSX.writeFile(wb, `Inventory_Recon_txn_${startDate}_to_${endDate}_inv_${invStartDate}_to_${invEndDate}.xlsx`);
    toast.success(`Downloaded ${sortedRows.length} SKUs`);
  };

  const resetFilters = () => { setSelectedBrands([]); setSelectedAsins([]); setSelectedSkus([]); };

  // ============ Render helpers ============
  const SortIcon = ({ active, dir }) => {
    const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return <Icon size={11} className={active ? 'text-primary' : 'text-slate-400'} />;
  };

  // Width helper so we can give exclusive widths to the 3 left frozen cols.
  // Now stateful so the user can drag-resize them at runtime. Defaults sized
  // for typical content; SKU widened to 360 so full names (e.g. "Creatine
  // Orange Wafer Bar (Pack of 1)") fit without truncation. Every column
  // header carries a resize handle on its right edge — see <ResizeHandle/>.
  const [colWidths, setColWidths] = useState({
    asin: 130, brand: 140, sku: 360, openingStock: 120,
    bulkDispatch: 130, b2cOrders: 130, rto: 130, rtv: 130,
    grn: 110, f2tof1: 110, idealClosing: 130, closingStock: 130, variance: 120,
    channelSub: 100,
  });
  // Drag-to-resize handle. Sits at the right edge of any <th>, intercepts
  // pointer events so it never triggers the sort click, and updates the
  // column's width live as the user drags. Min width 60 keeps headers legible.
  const ResizeHandle = ({ colKey }) => {
    const onPointerDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = colWidths[colKey] ?? 100;
      const move = (ev) => {
        const next = Math.max(60, startW + (ev.clientX - startX));
        setColWidths(prev => ({ ...prev, [colKey]: next }));
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        document.body.style.cursor = '';
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      document.body.style.cursor = 'col-resize';
    };
    return (
      <span
        onPointerDown={onPointerDown}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-white/40 active:bg-white/60 transition-colors"
        data-testid={`recon-resize-${colKey}`}
        title="Drag to resize column"
      />
    );
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-[1900px] mx-auto p-4 md:p-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-wv-ink">
              Inventory Recon
            </h1>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Open → Close variance · date-windowed totals
            </p>
          </div>
          <Button onClick={fetchAll} disabled={loading} data-testid="recon-refresh-btn">
            <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={16} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </header>

      <main className="max-w-[1900px] mx-auto p-4 md:p-8 space-y-6">
        {/* Filters */}
        <section data-testid="recon-filters" className="space-y-3">
          {/* Row 1 — Transaction window (drives flow metrics) */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">
              Transaction Range <span className="text-muted-foreground/60 normal-case tracking-normal">— Bulk Dispatch · B2C · RTO · RTV · GRN · F2→F1</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Txn Start Date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="recon-start-date" className="h-9" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Txn End Date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="recon-end-date" className="h-9" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Brand</Label>
                <MultiSelect testId="recon-brand-filter" options={brandOptions} selected={selectedBrands}
                  onChange={(v) => { setSelectedBrands(v); setSelectedAsins([]); setSelectedSkus([]); }}
                  placeholder="All Brands" emptyLabel="All Brands" searchable />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">ASIN</Label>
                <MultiSelect testId="recon-asin-filter" options={asinOptions} selected={selectedAsins}
                  onChange={(v) => { setSelectedAsins(v); setSelectedSkus([]); }}
                  placeholder="All ASINs" emptyLabel="All ASINs" searchable />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">SKU</Label>
                <MultiSelect testId="recon-sku-filter" options={skuOptions} selected={selectedSkus}
                  onChange={setSelectedSkus} placeholder="All SKUs" emptyLabel="All SKUs" searchable />
              </div>
            </div>
          </div>
          {/* Row 2 — Inventory window (drives Opening + Closing only) */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">
              Inventory Range <span className="text-muted-foreground/60 normal-case tracking-normal">— Opening (D-1 of start) · Closing (D-1 of end)</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Inv Start Date</Label>
                <Input type="date" value={invStartDate} onChange={(e) => setInvStartDate(e.target.value)} data-testid="recon-inv-start-date" className="h-9" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Inv End Date</Label>
                <Input type="date" value={invEndDate} onChange={(e) => setInvEndDate(e.target.value)} data-testid="recon-inv-end-date" className="h-9" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="text-[11px] text-muted-foreground" data-testid="recon-filter-summary">
              Txn {startDate} → {endDate} · Inv {invStartDate} → {invEndDate}
              {selectedBrands.length > 0 && ` · ${selectedBrands.length} brand${selectedBrands.length === 1 ? '' : 's'}`}
              {selectedAsins.length  > 0 && ` · ${selectedAsins.length} ASIN${selectedAsins.length === 1 ? '' : 's'}`}
              {selectedSkus.length   > 0 && ` · ${selectedSkus.length} SKU${selectedSkus.length === 1 ? '' : 's'}`}
              {` · ${sortedRows.length} SKU${sortedRows.length === 1 ? '' : 's'} in table`}
            </p>
            {(selectedBrands.length > 0 || selectedAsins.length > 0 || selectedSkus.length > 0) && (
              <button onClick={resetFilters} className="text-[11px] text-primary hover:underline" data-testid="recon-reset-filters">
                Clear filters
              </button>
            )}
          </div>
        </section>

        {/* Overview cards */}
        <section data-testid="recon-cards">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {RECON_METRICS.map(m => {
              const value = metricTotals[m.key] || 0;
              const isVariance = m.key === 'variance';
              const isDerived = m.source === 'derived';
              const isTbd = m.source === 'tbd';
              let outline = TONE_OUTLINE[m.tone] || TONE_OUTLINE.slate;
              let valueCls = 'text-wv-ink';
              if (isVariance) {
                if (value < 0) { outline = 'border-rose-500 ring-2 ring-rose-200'; valueCls = 'text-rose-600'; }
                else           { outline = 'border-emerald-500 ring-2 ring-emerald-200'; valueCls = 'text-emerald-700'; }
              }
              return (
                <Card key={m.key} className={`bg-white border-2 ${outline} ${isVariance ? 'shadow-md' : ''}`} data-testid={`recon-card-${m.key}`}>
                  <CardHeader className="pb-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-xs font-semibold uppercase tracking-widest ${isVariance ? 'text-rose-700' : 'text-muted-foreground'}`}>
                        {m.label}
                      </p>
                      {isTbd && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-slate-100 text-slate-600" title="Data source not mapped yet — see /lib/recon-config.js">TBD</span>}
                      {isDerived && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-sky-50 text-sky-700" title="Computed from other metrics">Σ</span>}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-2xl font-semibold tracking-tight tabular-nums ${valueCls}`} data-testid={`recon-value-${m.key}`}>
                      {isVariance && value > 0 ? '+' : ''}{formatInt(value)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground mt-4">
            <span className="font-medium text-wv-ink">Ideal Closing</span> = Opening − Bulk Dispatch − B2C Orders − F2→F1 + RTO + RTV + GRN
            &nbsp;·&nbsp;
            <span className="font-medium text-wv-ink">Variance</span> = Closing − Ideal Closing
          </p>
        </section>

        {/* SKU-level recon table */}
        <section data-testid="recon-table-section">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-sm font-semibold text-wv-ink">SKU-wise Recon</h2>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Click <Plus size={10} className="inline" /> on Bulk Dispatch · B2C Orders · RTO · RTV to reveal per-channel breakdown.
              </p>
            </div>
            <Button onClick={handleDownload} variant="outline" size="sm"
              disabled={sortedRows.length === 0} data-testid="recon-download-btn">
              <Download size={14} className="mr-2" />
              Download Excel
            </Button>
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
              <table className="w-full border-collapse text-sm" data-testid="recon-table">
                <thead className="sticky top-0 z-30">
                  <tr className="bg-primary text-primary-foreground">
                    {/* Frozen left cols */}
                    {[
                      { key: 'asin',  label: 'ASIN',  left: 0,                                 width: colWidths.asin  },
                      { key: 'brand', label: 'BRAND', left: colWidths.asin,                     width: colWidths.brand },
                      { key: 'sku',   label: 'SKU',   left: colWidths.asin + colWidths.brand,   width: colWidths.sku   },
                    ].map(c => (
                      <th key={c.key} rowSpan={2}
                        onClick={() => toggleSort(c.key)}
                        className="relative px-3 py-2 text-xs font-semibold uppercase tracking-widest cursor-pointer hover:opacity-90 sticky z-40 border-r border-primary/40"
                        style={{ left: c.left, width: c.width, minWidth: c.width, maxWidth: c.width, backgroundColor: '#2EB4AC' }}
                        data-testid={`recon-sort-${c.key}`}
                      >
                        <div className="flex items-center gap-1">
                          <span>{c.label}</span>
                          <SortIcon active={sortKey === c.key} dir={sortDir} />
                        </div>
                        <ResizeHandle colKey={c.key} />
                      </th>
                    ))}
                    {/* Opening Stock (single col) */}
                    <th rowSpan={2}
                      onClick={() => toggleSort('openingStock')}
                      className="relative px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider cursor-pointer hover:opacity-90 border-r border-primary/40"
                      style={{ backgroundColor: '#2EB4AC', width: colWidths.openingStock, minWidth: colWidths.openingStock, maxWidth: colWidths.openingStock }}
                      data-testid="recon-sort-openingStock"
                    >
                      <div className="flex items-center justify-end gap-1">
                        <span>Opening Stock</span>
                        <SortIcon active={sortKey === 'openingStock'} dir={sortDir} />
                      </div>
                      <ResizeHandle colKey="openingStock" />
                    </th>
                    {/* Expandable metric group headers — each section gets
                        its own color lane so Bulk Dispatch · B2C · RTO · RTV
                        stay grouped visually after expansion. */}
                    {EXPANDABLE_METRIC_KEYS.map(mk => {
                      const meta = RECON_METRICS.find(m => m.key === mk);
                      const expanded = expandedCols.has(mk);
                      const chList = channelsForMetric(mk);
                      const style = SECTION_STYLES[mk];
                      return (
                        <th key={mk}
                          colSpan={expanded ? 1 + chList.length : 1}
                          className="px-3 py-2 text-center text-[11px] font-bold uppercase tracking-[0.16em] border-l-2 border-white/40"
                          style={{ backgroundColor: style.header, color: style.text }}
                          data-testid={`recon-group-${mk}`}
                        >
                          <button
                            onClick={() => toggleExpand(mk)}
                            className="inline-flex items-center gap-1.5 hover:underline focus:outline-none"
                            title={expanded ? 'Collapse channel breakdown' : 'Expand channel breakdown'}
                            data-testid={`recon-expand-${mk}`}
                          >
                            {expanded ? <Minus size={11} /> : <Plus size={11} />}
                            <span>{meta.label}</span>
                          </button>
                        </th>
                      );
                    })}
                    {/* Trailing cols */}
                    {[
                      { key: 'grn',          label: 'GRN' },
                      { key: 'f2ToF1',       label: 'F2 → F1', widthKey: 'f2tof1' },
                      { key: 'idealClosing', label: 'Ideal Closing' },
                      { key: 'closingStock', label: 'Closing Stock' },
                      { key: 'variance',     label: 'Variance' },
                    ].map(c => {
                      const wk = c.widthKey || c.key;
                      const w = colWidths[wk];
                      return (
                        <th key={c.key} rowSpan={2}
                          onClick={() => toggleSort(c.key)}
                          className={`relative px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider cursor-pointer hover:opacity-90 border-l border-primary/40 ${c.key === 'variance' ? 'bg-rose-700' : ''}`}
                          style={c.key === 'variance'
                            ? { width: w, minWidth: w, maxWidth: w }
                            : { backgroundColor: '#2EB4AC', width: w, minWidth: w, maxWidth: w }}
                          data-testid={`recon-sort-${c.key}`}
                        >
                          <div className="flex items-center justify-end gap-1">
                            <span>{c.label}</span>
                            <SortIcon active={sortKey === c.key} dir={sortDir} />
                          </div>
                          <ResizeHandle colKey={wk} />
                        </th>
                      );
                    })}
                  </tr>
                  {/* Sub-header row: Total + channel sub-cols, only for expanded groups. */}
                  <tr className="text-white">
                    {EXPANDABLE_METRIC_KEYS.flatMap(mk => {
                      if (!expandedCols.has(mk)) return [];
                      const style = SECTION_STYLES[mk];
                      const cells = [];
                      // Total sub-col uses the shared bright highlight color
                      // so it pops against the section's color lane.
                      cells.push(
                        <th key={`${mk}-total`}
                          onClick={() => toggleSort(mk)}
                          className="relative px-3 py-2 text-right text-[10px] font-bold uppercase tracking-[0.14em] cursor-pointer hover:opacity-90 border-l-2 border-white/40"
                          style={{ backgroundColor: SECTION_TOTAL_HIGHLIGHT.header, color: SECTION_TOTAL_HIGHLIGHT.text, width: colWidths[mk], minWidth: colWidths[mk], maxWidth: colWidths[mk] }}
                          data-testid={`recon-subsort-${mk}-total`}
                        >
                          <div className="flex items-center justify-end gap-1">
                            <span>Total</span>
                            <SortIcon active={sortKey === mk} dir={sortDir} />
                          </div>
                          <ResizeHandle colKey={mk} />
                        </th>
                      );
                      channelsForMetric(mk).forEach(ch => {
                        const key = `ch:${mk}:${ch}`;
                        cells.push(
                          <th key={`${mk}-${ch}`}
                            onClick={() => toggleSort(key)}
                            className="relative px-2 py-2 text-right text-[10px] font-semibold tracking-wider cursor-pointer hover:opacity-90 border-l border-white/20"
                            style={{ backgroundColor: style.sub, color: style.text, width: colWidths.channelSub, minWidth: colWidths.channelSub, maxWidth: colWidths.channelSub }}
                            data-testid={`recon-subsort-${mk}-${ch.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <span>{ch}</span>
                              <SortIcon active={sortKey === key} dir={sortDir} />
                            </div>
                            <ResizeHandle colKey="channelSub" />
                          </th>
                        );
                      });
                      return cells;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 && (
                    <tr>
                      <td colSpan={50} className="text-center py-8 text-muted-foreground" data-testid="recon-empty">
                        No SKUs match the current filters
                      </td>
                    </tr>
                  )}
                  {sortedRows.map((r, ri) => {
                    const rowBg = ri % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                    return (
                      <tr key={`${r.asin || 'no-asin'}-${r.sku || ri}`} className={rowBg} data-testid={`recon-row-${ri}`}>
                        <td className={`px-3 py-2 text-xs text-wv-ink whitespace-nowrap sticky left-0 z-10 ${rowBg}`} style={{ width: colWidths.asin, minWidth: colWidths.asin, maxWidth: colWidths.asin }}>{r.asin || '—'}</td>
                        <td className={`px-3 py-2 text-xs text-wv-body whitespace-nowrap sticky z-10 ${rowBg}`} style={{ left: colWidths.asin, width: colWidths.brand, minWidth: colWidths.brand, maxWidth: colWidths.brand }}>{r.brand || '—'}</td>
                        <td className={`px-3 py-2 text-xs text-wv-ink sticky z-10 border-r-2 border-border ${rowBg}`} style={{ left: colWidths.asin + colWidths.brand, width: colWidths.sku, minWidth: colWidths.sku, maxWidth: colWidths.sku }} title={r.sku}>
                          {/* whitespace-normal + break-words so long SKU names
                              wrap rather than truncate. Drag the column wider
                              if a SKU still pushes onto multiple lines. */}
                          <span className="block whitespace-normal break-words leading-snug">{r.sku}</span>
                        </td>
                        <td className="px-3 py-2 text-right text-xs tabular-nums text-wv-body border-r border-border">{formatInt(r.openingStock)}</td>
                        {EXPANDABLE_METRIC_KEYS.flatMap(mk => {
                          const total = r[mk]?.total || 0;
                          const style = SECTION_STYLES[mk];
                          const cells = [];
                          // Total cell — shared highlight color (light amber)
                          // for instant recognition across all 4 sections.
                          cells.push(
                            <td key={`${mk}-total`}
                              className="px-3 py-2 text-right text-xs tabular-nums font-semibold border-l-2 border-border"
                              style={{ backgroundColor: SECTION_TOTAL_HIGHLIGHT.body, color: SECTION_TOTAL_HIGHLIGHT.text }}
                              data-testid={`recon-cell-${ri}-${mk}-total`}
                            >
                              {formatInt(total)}
                            </td>
                          );
                          if (expandedCols.has(mk)) {
                            channelsForMetric(mk).forEach(ch => {
                              const v = r[mk]?.byChannel?.[ch] || 0;
                              cells.push(
                                <td key={`${mk}-${ch}`}
                                  className="px-2 py-2 text-right text-xs tabular-nums border-l border-border/30"
                                  style={{ backgroundColor: style.body }}
                                  data-testid={`recon-cell-${ri}-${mk}-${ch.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  {formatInt(v)}
                                </td>
                              );
                            });
                          }
                          return cells;
                        })}
                        <td className="px-3 py-2 text-right text-xs tabular-nums text-wv-body border-l border-border">{formatInt(r.grn)}</td>
                        <td className="px-3 py-2 text-right text-xs tabular-nums text-wv-body border-l border-border">{formatInt(r.f2ToF1)}</td>
                        <td className="px-3 py-2 text-right text-xs tabular-nums text-sky-900 font-medium border-l border-border">{formatInt(r.idealClosing)}</td>
                        <td className="px-3 py-2 text-right text-xs tabular-nums text-wv-body border-l border-border">{formatInt(r.closingStock)}</td>
                        <td className={`px-3 py-2 text-right text-xs tabular-nums font-semibold border-l border-border ${r.variance < 0 ? 'text-rose-600' : r.variance > 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                          {r.variance > 0 ? '+' : ''}{formatInt(r.variance)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {sortedRows.length > 0 && (
                  <tfoot className="sticky bottom-0 z-20">
                    <tr className="bg-wv-ink text-white font-semibold">
                      <td className="px-3 py-2 text-xs uppercase tracking-widest sticky left-0 z-30 bg-wv-ink" style={{ width: colWidths.asin, minWidth: colWidths.asin, maxWidth: colWidths.asin }}>Total</td>
                      <td className="bg-wv-ink sticky z-30" style={{ left: colWidths.asin, width: colWidths.brand, minWidth: colWidths.brand, maxWidth: colWidths.brand }}>&nbsp;</td>
                      <td className="bg-wv-ink sticky z-30 border-r-2 border-white/20" style={{ left: colWidths.asin + colWidths.brand, width: colWidths.sku, minWidth: colWidths.sku, maxWidth: colWidths.sku }}>&nbsp;</td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums border-r border-white/20" data-testid="recon-total-openingStock">{formatInt(totals.openingStock)}</td>
                      {EXPANDABLE_METRIC_KEYS.flatMap(mk => {
                        const style = SECTION_STYLES[mk];
                        const cells = [];
                        cells.push(
                          <td key={`tot-${mk}`}
                            className="px-3 py-2 text-right text-xs tabular-nums font-bold border-l-2 border-white/30"
                            style={{ backgroundColor: SECTION_TOTAL_HIGHLIGHT.header, color: SECTION_TOTAL_HIGHLIGHT.text }}
                            data-testid={`recon-total-${mk}`}
                          >
                            {formatInt(totals[mk].total)}
                          </td>
                        );
                        if (expandedCols.has(mk)) {
                          channelsForMetric(mk).forEach(ch => {
                            cells.push(
                              <td key={`tot-${mk}-${ch}`}
                                className="px-2 py-2 text-right text-xs tabular-nums border-l border-white/15"
                                style={{ backgroundColor: style.sub, color: style.text }}
                                data-testid={`recon-total-${mk}-${ch.replace(/\s+/g, '-').toLowerCase()}`}
                              >
                                {formatInt(totals[mk].byChannel[ch])}
                              </td>
                            );
                          });
                        }
                        return cells;
                      })}
                      <td className="px-3 py-2 text-right text-xs tabular-nums border-l border-white/10" data-testid="recon-total-grn">{formatInt(totals.grn)}</td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums border-l border-white/10" data-testid="recon-total-f2ToF1">{formatInt(totals.f2ToF1)}</td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums border-l border-white/10" data-testid="recon-total-idealClosing">{formatInt(totals.idealClosing)}</td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums border-l border-white/10" data-testid="recon-total-closingStock">{formatInt(totals.closingStock)}</td>
                      <td className={`px-3 py-2 text-right text-xs tabular-nums border-l border-white/10 ${totals.variance < 0 ? 'text-rose-300' : totals.variance > 0 ? 'text-emerald-300' : ''}`} data-testid="recon-total-variance">
                        {totals.variance > 0 ? '+' : ''}{formatInt(totals.variance)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>
        </section>
      </main>
    </div>
  );
}
